import { performanceMonitor } from './performanceMonitor';
import { userExperienceTracker } from './userExperienceTracker';
import { backgroundSyncService } from './backgroundSyncService';
import { intelligentRetryService } from './intelligentRetryService';

class ProductionMonitor {
  private isProduction = import.meta.env.PROD;
  private reportingInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Only start in production and after DOM is loaded
    if (this.isProduction && typeof window !== 'undefined') {
      setTimeout(() => this.startMonitoring(), 1000);
    }
  }

  private startMonitoring(): void {
    // Report system health every 5 minutes
    this.reportingInterval = setInterval(() => {
      this.reportSystemHealth();
    }, 5 * 60 * 1000);

    // Report critical errors immediately
    window.addEventListener('error', (event) => {
      this.reportCriticalError(event.error);
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.reportCriticalError(event.reason);
    });
  }

  private async reportSystemHealth(): Promise<void> {
    try {
      const health = performanceMonitor.getSystemHealth();
      const uxMetrics = userExperienceTracker.getUXMetrics();
      const syncStats = backgroundSyncService.getTaskStats();
      const retryStats = intelligentRetryService.getRetryStats();

      const report = {
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        health: {
          score: health.score,
          trends: health.trends,
          predictions: health.predictions
        },
        ux: {
          sessionDuration: uxMetrics.sessionDuration,
          pageViews: uxMetrics.pageViews,
          errorRate: uxMetrics.errorRate,
          engagementScore: uxMetrics.engagementScore
        },
        sync: syncStats,
        retry: retryStats
      };

      // Send to monitoring service (replace with actual endpoint)
      await this.sendToMonitoring('health_report', report);
      
    } catch (error) {
      console.warn('Failed to report system health:', error);
    }
  }

  private async reportCriticalError(error: any): Promise<void> {
    try {
      const report = {
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name
        },
        systemHealth: performanceMonitor.getSystemHealth()
      };

      await this.sendToMonitoring('critical_error', report);
      
    } catch (reportError) {
      console.warn('Failed to report critical error:', reportError);
    }
  }

  private async sendToMonitoring(type: string, data: any): Promise<void> {
    // In production, this would send to your monitoring service
    // For now, we'll use background sync to queue it
    backgroundSyncService.addTask('analytics', {
      events: [{
        type: `production_${type}`,
        timestamp: Date.now(),
        data
      }]
    }, 'low');
  }

  // Public methods for manual reporting
  reportUserAction(action: string, data?: any): void {
    if (!this.isProduction) return;

    backgroundSyncService.addTask('analytics', {
      events: [{
        type: 'user_action',
        action,
        timestamp: Date.now(),
        url: window.location.href,
        data
      }]
    }, 'low');
  }

  reportPerformanceIssue(issue: string, metrics: any): void {
    if (!this.isProduction) return;

    backgroundSyncService.addTask('analytics', {
      events: [{
        type: 'performance_issue',
        issue,
        timestamp: Date.now(),
        url: window.location.href,
        metrics
      }]
    }, 'medium');
  }

  destroy(): void {
    if (this.reportingInterval) {
      clearInterval(this.reportingInterval);
      this.reportingInterval = null;
    }
  }
}

export const productionMonitor = new ProductionMonitor();
export default ProductionMonitor;
